import axios from "axios";
import {
  API_BASE_URL,
  CLIENT_NAME_HEADER_KEY,
  CLIENT_NAME_HEADER_VALUE,
  O_CLIENT_APP,
  O_API_KEY,
} from "constants";
import tokenRefreshService from "./tokenRefreshService";
import paths from "routes/paths";
import {
  getCurrentAppPath,
  savePostLoginRedirect,
} from "utils/postLoginRedirect";

const FILE_IDS_NOT_FOUND_PATTERN = /files?\s+with\s+fileids?.*not found/i;
const FILES_NOT_READY_MESSAGE =
  "Some uploaded files are not available yet. Please wait a few seconds and try again.";
const SERVER_ERROR_MESSAGE = "Something went wrong. Please try again later.";
const GLOBAL_SELECTED_WORKSPACE_KEY = "selectedWorkspaceId";
const GLOBAL_SELECTED_WORKSPACE_READY_KEY = "selectedWorkspaceReady";
const WORKSPACE_QUERY_PARAM = "wid";
const LEGACY_WORKSPACE_QUERY_PARAM = "workspaceId";
const SKIP_WORKSPACE_CONTEXT_HEADER = "O-SKIP-WORKSPACE-CONTEXT";
const WORKSPACE_ID_HEADER = "O-WORKSPACE-ID";
const WORKSPACE_WAIT_TIMEOUT_MS = 1800;
const WORKSPACE_WAIT_INTERVAL_MS = 50;
const TOKEN_STORAGE_KEYS = ["authToken", "accessToken", "olreadia.accessToken"];
const SESSION_STORAGE_KEYS = ["olreadia.session"];
const CLIENT_ID_KEY = "clientId";
const CLIENT_VALIDATED_KEY = "clientValidated";
const CLIENT_ID_ERROR_PATTERN = /(invalid\s+or\s+unknown\s+client\s+id|client-?id\s+header\s+is\s+missing\s+or\s+empty)/i;
const LEGACY_CLIENT_APP_CODE = "CARE_WEB_UI";
const AUTO_CLIENT_APP_CODE = "AUTO_WEB_UI";
let clientIdRegistrationPromise = null;
let validatedClientIdThisLoad = "";

const isServerErrorStatus = (status) => {
  const numericStatus = Number(status);
  return numericStatus >= 500 && numericStatus <= 599;
};

const normalizeBearerToken = (token) => {
  const trimmed = String(token || "").trim();
  if (!trimmed || ["undefined", "null", "false"].includes(trimmed.toLowerCase())) return "";
  return trimmed.startsWith("Bearer ") ? trimmed : `Bearer ${trimmed}`;
};

const readStorageValue = (storage, key) => {
  try {
    return storage.getItem(key);
  } catch {
    return "";
  }
};

const getStoredAuthToken = () => {
  for (const storage of [sessionStorage, localStorage]) {
    for (const key of TOKEN_STORAGE_KEYS) {
      const token = normalizeBearerToken(readStorageValue(storage, key));
      if (token) return token;
    }

    for (const key of SESSION_STORAGE_KEYS) {
      try {
        const session = JSON.parse(readStorageValue(storage, key) || "null");
        const token = normalizeBearerToken(session?.accessToken || session?.token || session?.jwt);
        if (token) return token;
      } catch {
        // Ignore malformed auth session values and keep checking other keys.
      }
    }
  }

  return "";
};

const isRequestCanceled = (error) => {
  if (!error) return false;

  const code = String(error.code || "").toUpperCase();
  const name = String(error.name || "");
  const message = String(error.message || "").toLowerCase();
  const requestStatus = Number(error.request?.status ?? 0);

  return (
    axios.isCancel?.(error) ||
    code === "ERR_CANCELED" ||
    code === "ABORT_ERR" ||
    code === "ECONNABORTED" ||
    name === "CanceledError" ||
    name === "AbortError" ||
    message.includes("cancel") ||
    message.includes("canceled") ||
    message.includes("cancelled") ||
    message.includes("aborted") ||
    (requestStatus === 0 && error.config?.signal?.aborted)
  );
};

const parseErrorPayload = (error) => {
  const status = error?.response?.status;
  const data = error?.response?.data;

  if (data && typeof data === "object") {
    return {
      status: status ?? data.code,
      message: data.message || data.error || null,
    };
  }

  if (typeof data === "string") {
    try {
      const parsed = JSON.parse(data);
      if (parsed && typeof parsed === "object") {
        return {
          status: status ?? parsed.code,
          message: parsed.message || parsed.error || data,
        };
      }
    } catch {
      return { status, message: data };
    }
    return { status, message: data };
  }

  return { status, message: null };
};

const normalizeResponseErrorMessage = (error) => {
  const { status, message } = parseErrorPayload(error);
  const nextMessage = isServerErrorStatus(status)
    ? SERVER_ERROR_MESSAGE
    : Number(status) === 404 && message && FILE_IDS_NOT_FOUND_PATTERN.test(message)
      ? FILES_NOT_READY_MESSAGE
      : message;

  if (!nextMessage) return { status, message };

  error.message = nextMessage;

  if (error.response) {
    const data = error.response.data;
    if (data && typeof data === "object") {
      error.response.data = {
        ...data,
        message: nextMessage,
        error: nextMessage,
      };
    } else {
      error.response.data = {
        message: nextMessage,
        error: nextMessage,
      };
    }
  }

  return { status, message: nextMessage };
};

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
    "O-API-KEY": O_API_KEY,
    "O-CLIENT-APP": O_CLIENT_APP,
    [CLIENT_NAME_HEADER_KEY]: CLIENT_NAME_HEADER_VALUE,
    "X-API-KEY": O_API_KEY, // for Projects Provider
  }
});

const isClientRegistrationRequest = (url = "") =>
  url.includes("/clients/register/request") ||
  url.includes("/clients/register/confirm");

const isClientValidationRequest = (url = "") => url.includes("/clients/validate");

const isClientManagementRequest = (url = "") =>
  isClientRegistrationRequest(url) || isClientValidationRequest(url);

const clearStoredClientId = () => {
  localStorage.removeItem(CLIENT_ID_KEY);
  localStorage.removeItem(CLIENT_VALIDATED_KEY);
};

const registerClientId = async () => {
  const requestResp = await axios.post(
    `${API_BASE_URL}/api/v1/clients/register/request`,
    {},
    {
      headers: {
        "Content-Type": "application/json",
        "O-API-KEY": O_API_KEY,
        "O-CLIENT-APP": O_CLIENT_APP,
        [SKIP_WORKSPACE_CONTEXT_HEADER]: "1",
      },
    },
  );

  const nextClientId = requestResp?.data?.id;
  if (!nextClientId) throw new Error("Client registration failed");

  const confirmResp = await axios.put(
    `${API_BASE_URL}/api/v1/clients/register/confirm`,
    {},
    {
      headers: {
        "Content-Type": "application/json",
        "O-API-KEY": O_API_KEY,
        "O-CLIENT-APP": O_CLIENT_APP,
        "O-CLIENT-ID": nextClientId,
        [SKIP_WORKSPACE_CONTEXT_HEADER]: "1",
      },
    },
  );

  if (confirmResp?.data?.id !== nextClientId) {
    throw new Error("Client confirmation failed");
  }

  localStorage.setItem(CLIENT_ID_KEY, nextClientId);
  localStorage.setItem(CLIENT_VALIDATED_KEY, "true");
  validatedClientIdThisLoad = nextClientId;
  return nextClientId;
};

const validateClientId = async (clientId) => {
  if (!clientId) return false;

  try {
    const response = await axios.get(`${API_BASE_URL}/api/v1/clients/validate`, {
      headers: {
        "Content-Type": "application/json",
        "O-API-KEY": O_API_KEY,
        "O-CLIENT-APP": O_CLIENT_APP,
        "O-CLIENT-ID": clientId,
        [SKIP_WORKSPACE_CONTEXT_HEADER]: "1",
      },
    });

    return Boolean(response?.data?.id || response?.data?.valid === true);
  } catch {
    return false;
  }
};

const ensureRegisteredClientId = async () => {
  const existing = localStorage.getItem(CLIENT_ID_KEY);
  if (existing && validatedClientIdThisLoad === existing) return existing;
  if (existing) {
    const isValid = await validateClientId(existing);
    if (isValid) {
      localStorage.setItem(CLIENT_VALIDATED_KEY, "true");
      validatedClientIdThisLoad = existing;
      return existing;
    }
    clearStoredClientId();
  }

  if (!clientIdRegistrationPromise) {
    clientIdRegistrationPromise = registerClientId().finally(() => {
      clientIdRegistrationPromise = null;
    });
  }

  return clientIdRegistrationPromise;
};

const selectedWorkspaceId = () => {
  const direct = localStorage.getItem(GLOBAL_SELECTED_WORKSPACE_KEY);
  if (direct) return direct;

  for (let index = 0; index < localStorage.length; index += 1) {
    const key = localStorage.key(index);
    if (key?.startsWith("selectedWorkspaceId:")) {
      const value = localStorage.getItem(key);
      if (value) return value;
    }
  }

  return "";
};

const workspaceSelectionReady = () =>
  localStorage.getItem(GLOBAL_SELECTED_WORKSPACE_READY_KEY) === "1";

const waitForWorkspaceSelection = async () => {
  if (selectedWorkspaceId() || workspaceSelectionReady()) {
    return selectedWorkspaceId();
  }

  const startedAt = Date.now();
  while (Date.now() - startedAt < WORKSPACE_WAIT_TIMEOUT_MS) {
    await new Promise((resolve) => setTimeout(resolve, WORKSPACE_WAIT_INTERVAL_MS));
    const workspaceId = selectedWorkspaceId();
    if (workspaceId || workspaceSelectionReady()) {
      return workspaceId;
    }
  }

  return selectedWorkspaceId();
};

const withWorkspaceParam = (params, workspaceId) => {
  if (!workspaceId) return params;
  if (params instanceof URLSearchParams) {
    const nextParams = new URLSearchParams(params);
    nextParams.set(WORKSPACE_QUERY_PARAM, workspaceId);
    return nextParams;
  }

  const nextParams = {
    ...(params || {}),
    [WORKSPACE_QUERY_PARAM]: workspaceId,
  };

  return nextParams;
};

const replaceLegacyClientApp = (value) =>
  typeof value === "string" ? value.replaceAll(LEGACY_CLIENT_APP_CODE, AUTO_CLIENT_APP_CODE) : value;

const normalizeClientAppParams = (params) => {
  if (!params) return params;

  if (params instanceof URLSearchParams) {
    const nextParams = new URLSearchParams(params);
    for (const [key, value] of nextParams.entries()) {
      const nextValue = replaceLegacyClientApp(value);
      if (nextValue !== value) {
        nextParams.set(key, nextValue);
      }
    }
    return nextParams;
  }

  if (typeof params === "object") {
    return Object.fromEntries(
      Object.entries(params).map(([key, value]) => [
        key,
        replaceLegacyClientApp(value),
      ]),
    );
  }

  return params;
};

const appendWorkspaceContext = async (config) => {
  const workspaceId = await waitForWorkspaceSelection();
  if (workspaceId) {
    config.params = withWorkspaceParam(config.params, workspaceId);
    config.headers[WORKSPACE_ID_HEADER] = workspaceId;
  }
};

const prepareRequestConfig = async (config) => {
  const token = getStoredAuthToken();
  config.headers = config.headers || {};
  const skipWorkspaceContext = Boolean(config.headers[SKIP_WORKSPACE_CONTEXT_HEADER]);
  const skipAuthContext = Boolean(config.skipAuthContext);
  if (skipWorkspaceContext) {
    delete config.headers[SKIP_WORKSPACE_CONTEXT_HEADER];
  }
  config.headers["O-CLIENT-APP"] = config.headers["O-CLIENT-APP"] || O_CLIENT_APP;
  config.headers[CLIENT_NAME_HEADER_KEY] =
    config.headers[CLIENT_NAME_HEADER_KEY] || CLIENT_NAME_HEADER_VALUE;
  config.headers["O-CLIENT-APP"] = replaceLegacyClientApp(config.headers["O-CLIENT-APP"]);
  config.headers[CLIENT_NAME_HEADER_KEY] = replaceLegacyClientApp(config.headers[CLIENT_NAME_HEADER_KEY]);
  config.url = replaceLegacyClientApp(config.url);
  config.params = normalizeClientAppParams(config.params);
  config.headers["O-API-KEY"] = O_API_KEY;
  config.headers["X-API-KEY"] = O_API_KEY;

  const isClientManagement = isClientManagementRequest(config.url || "");

  const clientId = isClientManagement ? "" : await ensureRegisteredClientId().catch(() => "");
  if (clientId && !isClientManagement) {
    config.headers["O-CLIENT-ID"] = clientId;
  }

  if (token && !skipAuthContext && !config.headers.Authorization) {
    config.headers.Authorization = token;
  }

  if (!skipWorkspaceContext) {
    await appendWorkspaceContext(config);
  }

  return config;
};

axios.interceptors.request.use(prepareRequestConfig);
axios.interceptors.response.use(
  (res) => res,
  (error) => {
    if (isRequestCanceled(error)) {
      return Promise.resolve();
    }

    normalizeResponseErrorMessage(error);
    return Promise.reject(error);
  }
);
apiClient.interceptors.request.use(prepareRequestConfig);

apiClient.interceptors.response.use(
  (res) => res,
  async (error) => {
    if (isRequestCanceled(error)) {
      return Promise.resolve();
    }

    const original = error.config;
    const { message } = normalizeResponseErrorMessage(error);

    if (
      message &&
      CLIENT_ID_ERROR_PATTERN.test(message) &&
      original &&
      !original._clientIdRetry &&
      !isClientManagementRequest(original.url || "")
    ) {
      original._clientIdRetry = true;
      clearStoredClientId();

      try {
        const nextClientId = await ensureRegisteredClientId();
        original.headers = original.headers || {};
        original.headers["O-CLIENT-ID"] = nextClientId;
        return apiClient(original);
      } catch {
        clearStoredClientId();
      }
    }

    if (
      error.response?.status === 401 &&
      !original._retry &&
      sessionStorage.getItem("refreshToken")
    ) {
      original._retry = true;

      try {
        await tokenRefreshService.refreshTokens();
        original.headers.Authorization = getStoredAuthToken();
        return apiClient(original);
      } catch {
        savePostLoginRedirect(getCurrentAppPath());
        window.location.href = paths.simpleSessionExpired;
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
